import { parseHandoffs } from "@/shared/lib/advisorHandoff";
import type { AdvisorTurnData, ChatState, ConfirmGate, StyleGalleryItem } from "./chatTypes";

export function initialChat(): ChatState {
  return { messages: [], pendingConfirm: null };
}

export function appendUser(s: ChatState, text: string): ChatState {
  return { ...s, messages: [...s.messages, { role: "user", text }] };
}

/** actions 里全部合法 style_gallery 合并成一份(按 key 去重):后端一 turn 可能弹多组
 *  (如「商务和二次元各推几个」两次 show),只取首个会静默丢后组(红队 2026-07-09)。 */
function styleGalleryFrom(actions: unknown[] | undefined): StyleGalleryItem[] | undefined {
  const items: StyleGalleryItem[] = [];
  const seen = new Set<string>();
  for (const a of actions ?? []) {
    if (!a || typeof a !== "object" || (a as { type?: unknown }).type !== "style_gallery") continue;
    const styles = (a as { styles?: unknown }).styles;
    if (!Array.isArray(styles)) continue;
    for (const it of styles) {
      if (
        !!it &&
        typeof it === "object" &&
        typeof (it as { key?: unknown }).key === "string" &&
        typeof (it as { label?: unknown }).label === "string" &&
        !seen.has((it as StyleGalleryItem).key)
      ) {
        seen.add((it as StyleGalleryItem).key);
        items.push(it as StyleGalleryItem);
      }
    }
  }
  return items.length > 0 ? items : undefined;
}

/** 应用一次 turn/resume 富回复:有 text 则追加军师气泡;confirm 非空挂起确认门,否则清门。 */
export function applyTurn(s: ChatState, turn: AdvisorTurnData): ChatState {
  const styleGallery = styleGalleryFrom(turn.actions);
  // ADV-HANDOFF-PC-1:交接卡与风格卡同规解析(共享解析器,两端同一份)。
  const handoffs = parseHandoffs(turn.actions);
  const messages =
    turn.text || styleGallery || handoffs
      ? [
          ...s.messages,
          {
            role: "advisor" as const,
            text: turn.text,
            evidence: turn.evidence,
            ...(styleGallery ? { styleGallery } : {}),
            ...(handoffs ? { handoffs } : {}),
          },
        ]
      : s.messages;
  return { messages, pendingConfirm: turn.confirm ?? null };
}

/** 上一张确认卡被新回复顶掉时插进对话流的那一句(B2 #8⑦)。 */
export const CONFIRM_SUPERSEDED_TEXT = "上一张确认卡已作废,以下面这条回复为准。";

/**
 * 用户在确认卡挂着时又发了一句 → 后端按新话开一轮,旧卡的 nonce 随之失效(resume 会被拒)。
 * 此前前端只是把 pendingConfirm 静默换掉/清掉,用户看不出那张卡怎么没了。
 * ★只在 **send 路**用(resume/pick-style 是对旧卡本身的处置,不算「顶掉」)。
 * 判据:原来有卡,且新 turn 没带卡、或带的是另一张(id 不同)。
 */
export function noteSupersededConfirm(s: ChatState, turn: AdvisorTurnData): ChatState {
  const prior = s.pendingConfirm;
  if (!prior) return s;
  const replaced = !turn.confirm || turn.confirm.id !== prior.id;
  if (!replaced) return s;
  // 1004 F23b:ai 侧已在正文前确定性加了「(上一张确认卡已作废。)」→ 这里不再插第二句
  //(演示机复测:同一屏连写两遍「上一张确认卡已作废」)。
  if ((turn.text ?? "").includes("确认卡已作废")) return s;
  return {
    ...s,
    messages: [...s.messages, { role: "advisor", text: CONFIRM_SUPERSEDED_TEXT }],
  };
}

/**
 * 服务端探针交回来的待确认卡能不能摆上屏(1004 A1)。只认结构:有 nonce(id)和工具名。
 * ★认不出就当没有 —— 摆一张残缺的卡(点了 resume 带空 nonce)比不摆更糟。
 */
export function isConfirmGate(v: unknown): v is ConfirmGate {
  if (!v || typeof v !== "object") return false;
  const o = v as { id?: unknown; tool?: unknown };
  return typeof o.id === "string" && o.id.length > 0 && typeof o.tool === "string";
}

/**
 * 确认卡上方那段「卡面复述」(1004 F23b)。
 * ai 开卡那一轮的正文就是卡面原话(`{summary} · {价},确认做吗?`,为了落进对话记录),
 * 而确认卡本身又把 summary 和价格摆一遍 —— 卡还挂着时同一段话上下各出现一次。
 * 卡在场时把正文里这段复述摘掉;摘完没剩东西就整条不显示。卡没了(作废/已确认)就原样显示,
 * 那时它是对话记录里唯一说明「当时要做什么」的一句。
 */
export function stripConfirmEcho(text: string, confirm: ConfirmGate | null | undefined): string {
  const summary = (confirm?.quote?.summary ?? "").trim();
  if (!text || !summary) return text;
  const at = text.indexOf(summary);
  if (at < 0) return text;
  let end = at + summary.length;
  // 价格那半句(「 · 约 28 积分,确认做吗?」)也归卡面:到第一处「确认做吗?」为止一并摘掉
  const ask = /^[^\n]{0,80}?确认做吗[??]?/.exec(text.slice(end));
  if (ask) end += ask[0].length;
  return (text.slice(0, at) + text.slice(end)).trim();
}
